import { useEffect, useRef, useState } from "react";
import TaskCard from "./TaskCard"; // Explicitly added .jsx extension

const KanbanColumn = ({
  column,
  title,
  tasks,
  onAddTaskClick,
  onEditTaskClick,
  onDropTask,
  onDeleteColumn,
  onViewCommentsClick,
}) => {
  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add("drag-over");
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove("drag-over");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove("drag-over");
    const taskId = e.dataTransfer.getData("taskId");
    const sourceColumnId = e.dataTransfer.getData("sourceColumnId");
    onDropTask(taskId, sourceColumnId, column.id, column.title);
  };

  const [showOptions, setShowOptions] = useState(false);
  const optionsRef = useRef(null);

  // Close options menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (optionsRef.current && !optionsRef.current.contains(event.target)) {
        setShowOptions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [optionsRef]);

  // Count leads vs regular tasks
  const leadCount = tasks.filter(
    (task) => task.leadData && task.leadData.source
  ).length;
  const regularTaskCount = tasks.length - leadCount;

  // Get column status color
  const getColumnStatusColor = () => {
    if (
      column.title.toLowerCase().includes("completed") ||
      column.title.toLowerCase().includes("done")
    ) {
      return "from-green-900/20 to-emerald-900/20 border-green-500/30";
    }
    if (
      column.title.toLowerCase().includes("progress") ||
      column.title.toLowerCase().includes("doing")
    ) {
      return "from-yellow-900/20 to-orange-900/20 border-yellow-500/30";
    }
    if (
      column.title.toLowerCase().includes("review") ||
      column.title.toLowerCase().includes("testing")
    ) {
      return "from-purple-900/20 to-indigo-900/20 border-purple-500/30";
    }
    return "from-gray-900/20 to-slate-900/20 border-gray-500/30";
  };

  return (
    <div
      className={`flex-shrink-0 w-80 bg-gradient-to-br ${getColumnStatusColor()} rounded-lg p-4 m-2 shadow-lg border hover:border-opacity-50 transition-all duration-200 h-auto min-h-[calc(100vh-180px)] flex flex-col`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Column Header */}
      <div className="flex justify-between items-center mb-4 relative">
        <div className="flex  items-center gap-1">
          <h3 className="text-white text-lg font-semibold mb-1 text-ce">
            {column.title}
          </h3>
          <div className="flex w-5 h-5 items-center justify-center text-sm  border border-gray-600 rounded  bg-[#181f53]">
            {tasks.length}
          </div>
        </div>

        <div ref={optionsRef} className="relative">
          <button
            onClick={() => setShowOptions(!showOptions)}
            className="text-gray-400 hover:text-white p-2 rounded-full hover:bg-gray-700/50 transition-all duration-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z"
              />
            </svg>
          </button>
          {showOptions && (
            <div className="absolute right-0 mt-2 w-48 bg-[#242940] rounded-lg shadow-lg border border-gray-600/50 py-2 z-10">
              <button
                onClick={() => {
                  onDeleteColumn(column.id);
                  setShowOptions(false);
                }}
                className="flex items-center w-full text-left px-4 py-2 text-sm text-red-300 hover:bg-red-800/20 hover:text-red-200 transition-colors duration-200"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                Delete Column
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Add Task Button */}
      <button
        onClick={() => onAddTaskClick(column.id)}
        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center mb-4 transition-all duration-200 shadow-md hover:shadow-lg border border-blue-500/30"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 mr-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
          />
        </svg>
        Add Task
      </button>

      {/* Tasks Container */}
      <div className="space-y-3 max-h-[65dvh] flex-grow overflow-y-auto pr-1 custom-scrollbar">
        {tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            onEditTaskClick={onEditTaskClick}
            onViewCommentsClick={onViewCommentsClick}
          />
        ))}
        {tasks.length === 0 && (
          <div className="flex flex-col items-center justify-center text-gray-500 text-center py-8">
            <div className="bg-gray-700/30 rounded-full p-4 mb-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012-2"
                />
              </svg>
            </div>
            <p className="text-sm font-medium">No lead yet</p>
            <p className="text-xs text-gray-600 mt-1">
              Drag a lead here or add a new one!
            </p>
          </div>
        )}
      </div>

      {/* Column Footer Stats */}
      {tasks.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-600/30">
          <div className="flex justify-between items-center text-xs text-gray-400">
            <div className="flex items-center space-x-3">
              {regularTaskCount > 0 && <span>📋 {regularTaskCount} tasks</span>}
              {leadCount > 0 && <span>🎯 {leadCount} leads</span>}
            </div>
            <div className="text-gray-500">Total: {tasks.length}</div>
          </div>
        </div>
      )}

      {/* Drag Over Indicator */}
      <style jsx>{`
        .drag-over {
          transform: scale(1.02);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
          border-color: rgba(59, 130, 246, 0.5) !important;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(75, 85, 99, 0.1);
          border-radius: 3px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(75, 85, 99, 0.5);
          border-radius: 3px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(75, 85, 99, 0.7);
        }
      `}</style>
    </div>
  );
};

export default KanbanColumn;
